import React, { useEffect, useMemo, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { Info, X } from "lucide-react";
import toast from "react-hot-toast";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import RHFInput from "@/components/form/RHFInput";
import RHFTextArea from "@/components/form/RHFTextArea";
import RHFSelect from "@/components/form/RHFSelect";
import { Callout } from "@/components/ui/Callout";

import CandidateStatusService from "@/services/candidateStatusService";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import { getCandidateStatusTypeMeta } from "../constants";
import { getStatusType } from "../statusFlags";
import {
  PLACEMENT_END,
  buildPlacementOptions,
  resolvePlacementSequence,
} from "../placement";

import { candidateStatusValidationSchema } from "../validation.schema";

const toText = (value) => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  return "";
};

const Toggle = ({ checked, onChange, label, disabled = false, hint = "" }) => (
  <label
    className={`flex items-start gap-3 rounded-xl border border-border-primary bg-background-secondary p-3 ${
      disabled ? "opacity-60" : "cursor-pointer"
    }`}
  >
    <Input
      type="checkbox"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange(e.target.checked)}
      className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--color-btn-secondary))]"
    />
    <div className="min-w-0 flex-1">
      <div className="text-xs font-medium text-text-primary">{toText(label)}</div>
      {hint ? <div className="mt-1 text-[11px] text-text-secondary">{hint}</div> : null}
    </div>
  </label>
);

const AddEditCandidateStatusModal = ({
  isOpen,
  mode = "add",
  defaultValues,
  selectedStatus,
  flowId,
  onModelClose,
  onHandleSubmit,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [allStatuses, setAllStatuses] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  // Whether `allStatuses` is this flow's real list yet. Without it, the empty
  // array a fresh mount starts with — and the one left over from the previous
  // open — both read as "this flow has no statuses", which forced Initial on
  // every status after the first (i1766).
  const [optionsLoaded, setOptionsLoaded] = useState(false);
  const [inlineError, setInlineError] = useState("");

  const isEdit = mode === "edit";

  const isFirstStatusInFlow = !isEdit && optionsLoaded && allStatuses.length === 0;

  const methods = useForm({
    resolver: yupResolver(candidateStatusValidationSchema),
    defaultValues,
    mode: "onSubmit",
    context: { mode },
  });

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = methods;

  const selectedTransitions = watch("allowedTransitionIds") || [];
  const isInitial = !!watch("isInitial");
  const isHired = !!watch("isHired");
  const isRejected = !!watch("isRejected");
  const isReusable = !!watch("isReusable");
  const isInterviewEligible = !!watch("isInterviewEligible");
  const selectedSeq = watch("sequence");
  const insertBeforeId = watch("insertBeforeId");

  useEffect(() => {
    if (isOpen) {
      reset(defaultValues);
      setInlineError("");
      setAllStatuses([]);
      setOptionsLoaded(false);
    }
  }, [defaultValues, isOpen, reset]);

  useEffect(() => {
    const fetchOptions = async () => {
      if (!isOpen || !flowId) return;

      try {
        setLoadingOptions(true);
        const res = await CandidateStatusService.list({
          flowId,
          page: 1,
          per_page: 100,
          sort_by: "sequence",
          sort_order: "asc",
        });

        const list = Array.isArray(res?.data)
          ? res.data
          : Array.isArray(res?.data?.data)
            ? res.data.data
            : [];

        setAllStatuses(list.filter((item) => item?.id !== selectedStatus?.id));
      } catch (e) {
        toast.error(apiErrorMessageFormat(e, "Failed to load statuses"));
      } finally {
        setLoadingOptions(false);
        setOptionsLoaded(true);
      }
    };

    fetchOptions();
  }, [isOpen, flowId, selectedStatus?.id]);

  const sortedStatuses = useMemo(
    () => [...allStatuses].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [allStatuses]
  );

  useEffect(() => {
    if (!isRejected && !isHired) return;
    setValue("allowedTransitionIds", [], {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [isRejected, isHired, setValue]);

  useEffect(() => {
    // Nothing is decidable until the flow's own statuses are in hand: both
    // "is this the first status" and "what sequence does this placement mean"
    // read the list.
    if (isEdit || !optionsLoaded) return;

    // First status in an empty flow must be Initial
    if (isFirstStatusInFlow) {
      setValue("isInitial", true, { shouldValidate: true, shouldDirty: true });
      setValue("sequence", 1, { shouldValidate: true, shouldDirty: true });
      setValue("insertBeforeId", null, { shouldValidate: false, shouldDirty: true });
      return;
    }

    // Every placement revalidates freely — `resolvePlacementSequence` only
    // ever returns a sequence this flow accepts, so re-running the schema also
    // clears an error left over from a failed submit.
    if (isInitial) {
      setValue("sequence", 1, { shouldValidate: true, shouldDirty: true });
      setValue("insertBeforeId", null, { shouldValidate: false, shouldDirty: true });
      return;
    }

    // Appending is the placement every flow can always take, so it is where an
    // unset (or since-deleted) choice lands rather than leaving the form with
    // no sequence at all.
    if (insertBeforeId === null || insertBeforeId === undefined) {
      setValue("insertBeforeId", PLACEMENT_END, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    setValue("sequence", resolvePlacementSequence(insertBeforeId, sortedStatuses), {
      shouldValidate: true,
      shouldDirty: true,
    });
  }, [insertBeforeId, isInitial, isEdit, optionsLoaded, setValue, sortedStatuses, isFirstStatusInFlow]);

  const currentSeq = isEdit ? selectedStatus?.sequence ?? 0 : selectedSeq ?? 0;

  const transitionOptions = useMemo(
    () =>
      sortedStatuses
        .filter((item) => {
          if (item?.isReusable) return true;
          return (item?.sequence ?? 0) > currentSeq;
        })
        .map((item) => ({
          label: toText(item?.name) || "—",
          value: item?.id,
          code: item?.code,
          sequence: item?.sequence,
          // One precedence chain for every pill in the app — the picker's
          // type label reads the same source as the table and view modal.
          type: getCandidateStatusTypeMeta(getStatusType(item)).label,
        })),
    [sortedStatuses, currentSeq]
  );

  const placementOptions = useMemo(
    () => buildPlacementOptions(sortedStatuses),
    [sortedStatuses]
  );

  const placementPreview = useMemo(() => {
    if (isEdit) {
      return selectedStatus?.sequence
        ? `This status is fixed at step ${selectedStatus.sequence}.`
        : "Sequence is fixed after creation.";
    }

    if (isInitial) {
      return "This will be created as the initial status at sequence 1.";
    }

    const beforeItem = sortedStatuses.find(
      (item) => item.id === insertBeforeId && Number(item.sequence) > 1
    );

    if (beforeItem) {
      return `This will be inserted before "${beforeItem.name}" at step ${beforeItem.sequence}.`;
    }

    return `This will be added at the end of the pipeline, as step ${resolvePlacementSequence(
      PLACEMENT_END,
      sortedStatuses
    )}.`;
  }, [isEdit, isInitial, selectedStatus, sortedStatuses, insertBeforeId]);

  const handleTransitionToggle = (id) => {
    const current = new Set(selectedTransitions);
    if (current.has(id)) current.delete(id);
    else current.add(id);

    setValue("allowedTransitionIds", Array.from(current), {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleToggleRole = (field, value) => {
    if (field === "isInitial") {
      setValue("isInitial", value, { shouldDirty: true, shouldValidate: true });
      if (value) {
        setValue("isHired", false, { shouldDirty: true, shouldValidate: true });
        setValue("isRejected", false, { shouldDirty: true, shouldValidate: true });
        setValue("isReusable", false, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    if (field === "isHired") {
      setValue("isHired", value, { shouldDirty: true, shouldValidate: true });
      if (value) {
        setValue("isInitial", false, { shouldDirty: true, shouldValidate: true });
        setValue("isRejected", false, { shouldDirty: true, shouldValidate: true });
        setValue("isReusable", false, { shouldDirty: true, shouldValidate: true });
        setValue("isInterviewEligible", false, { shouldDirty: true, shouldValidate: true });
        setValue("allowedTransitionIds", [], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      return;
    }

    if (field === "isRejected") {
      setValue("isRejected", value, { shouldDirty: true, shouldValidate: true });
      if (value) {
        setValue("isInitial", false, { shouldDirty: true, shouldValidate: true });
        setValue("isHired", false, { shouldDirty: true, shouldValidate: true });
        setValue("isReusable", false, { shouldDirty: true, shouldValidate: true });
        setValue("isInterviewEligible", false, { shouldDirty: true, shouldValidate: true });
        setValue("allowedTransitionIds", [], {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
      return;
    }

    if (field === "isReusable") {
      setValue("isReusable", value, { shouldDirty: true, shouldValidate: true });
      if (value) {
        setValue("isInitial", false, { shouldDirty: true, shouldValidate: true });
        setValue("isHired", false, { shouldDirty: true, shouldValidate: true });
        setValue("isRejected", false, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }

    if (field === "isInterviewEligible") {
      setValue("isInterviewEligible", value, { shouldDirty: true, shouldValidate: true });
      if (value) {
        // Only mutually exclusive with Hired/Rejected (terminal outcomes) —
        // a status can still be Initial/Reusable AND interview-eligible.
        setValue("isHired", false, { shouldDirty: true, shouldValidate: true });
        setValue("isRejected", false, { shouldDirty: true, shouldValidate: true });
      }
      return;
    }
  };

  const submitForm = async (values) => {
    try {
      setSubmitting(true);
      setInlineError("");

      const payload = {
        ...values,
        sequence: isEdit ? undefined : values?.sequence,
        allowedTransitionIds:
          values?.isRejected || values?.isHired ? [] : values?.allowedTransitionIds ?? [],
      };

      delete payload.insertBeforeId;

      await onHandleSubmit(payload);
    } catch (e) {
      setInlineError(
        e?.response?.data?.detail ||
          apiErrorMessageFormat(e, "Failed to save candidate status")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && onModelClose?.()}>
      <DialogContent className="flex w-[95vw] max-w-[760px] max-h-[90vh] flex-col overflow-hidden p-0">
        <DialogHeader className="relative shrink-0 border-b border-border-primary px-5 py-3">
          <div className="pr-10">
            <DialogTitle className="text-[15px] font-semibold text-text-primary">
              {isEdit ? "Edit Candidate Status" : "Add Candidate Status"}
            </DialogTitle>

            <DialogDescription className="mt-0.5 text-[12px] text-text-primary/70">
              {isEdit
                ? "Update workflow status details. Step is fixed after creation."
                : "Choose where this status goes in the pipeline — before an existing step, or at the end."}
            </DialogDescription>
          </div>

          <DialogClose className="absolute right-5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-background-icon/40">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(submitForm)} className="flex min-h-0 flex-1 flex-col">
            <div className="custom-scrollbar flex-1 min-h-0 space-y-5 overflow-y-auto p-5">
              {isEdit && selectedStatus?.sequence ? (
                <div className="rounded-xl border border-border-primary bg-background-form px-4 py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[11px] text-text-secondary">Current Step</span>
                      <p className="mt-0.5 text-sm font-semibold text-text-primary">
                        Step {selectedStatus.sequence}
                      </p>
                    </div>
                    <span className="text-[10px] text-text-secondary/60">Fixed at creation</span>
                  </div>
                </div>
              ) : null}

              {!isEdit ? (
                <div className="space-y-3">
                  <label className="block text-sm text-text-secondary">
                    Placement in Pipeline
                    <span className="ml-1 text-[16px] font-bold text-red-500">*</span>
                  </label>

                  {!optionsLoaded ? (
                    <div className="w-full rounded-xl border border-border-primary bg-background-form/60 px-3 py-2.5 text-sm text-text-secondary opacity-60">
                      Loading this flow's pipeline...
                    </div>
                  ) : isFirstStatusInFlow ? (
                    <>
                      <div className="w-full rounded-xl border border-border-primary bg-background-form/60 px-3 py-2.5 text-sm text-text-secondary opacity-60">
                        First status in a flow must be Initial
                      </div>
                      <div className="rounded-xl border border-border-primary bg-background-form/60 px-3 py-2 text-[11px] text-text-secondary">
                        {placementPreview}
                      </div>
                    </>
                  ) : isInitial ? (
                    <>
                      <div className="w-full rounded-xl border border-border-primary bg-background-form/60 px-3 py-2.5 text-sm text-text-secondary opacity-60">
                        Initial status is always placed at step 1
                      </div>
                      <div className="rounded-xl border border-border-primary bg-background-form/60 px-3 py-2 text-[11px] text-text-secondary">
                        {placementPreview}
                      </div>
                    </>
                  ) : (
                    <>
                      <RHFSelect
                        name="insertBeforeId"
                        placeholder="Choose a placement"
                        options={placementOptions}
                        // The picker's value is a sentinel ("place at the
                        // end") as often as it is a real status id, which is
                        // what these two overrides are for.
                        value={insertBeforeId ?? PLACEMENT_END}
                        onChange={(value) =>
                          setValue("insertBeforeId", value || PLACEMENT_END, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }
                      />
                      <div className="rounded-xl border border-border-primary bg-background-form/60 px-3 py-2 text-[11px] text-text-secondary">
                        {placementPreview}
                      </div>
                    </>
                  )}

                  {errors?.sequence ? (
                    <p className="text-xs text-red-500">{errors.sequence.message}</p>
                  ) : null}
                </div>
              ) : (
                <div>
                  <label className="mb-1.5 block text-sm text-text-secondary">
                    Placement in Pipeline
                  </label>
                  <div className="w-full rounded-xl border border-border-primary bg-background-form/60 px-3 py-2.5 text-sm text-text-secondary">
                    Step is fixed at creation
                  </div>
                </div>
              )}

              <RHFInput
                name="name"
                label="Name"
                placeholder="Enter status name"
                required
              />

              <RHFTextArea
                name="description"
                label="Description"
                placeholder="Enter status description"
                required
                rows={4}
                maxLength={500}
                inputClass="max-h-[160px] overflow-y-auto resize-y"
              />

              <div className="rounded-2xl border border-border-primary bg-background-form p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-text-primary">Status Type</h3>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    Initial, Hired, and Rejected are mutually exclusive.
                  </p>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Toggle
                    label="Mark as Initial"
                    checked={isInitial}
                    onChange={(value) => handleToggleRole("isInitial", value)}
                    disabled={isHired || isRejected || isReusable || isFirstStatusInFlow}
                    hint={isFirstStatusInFlow ? "First status in a flow must be Initial" : "Starting status of the workflow."}
                  />
                  <Toggle
                    label="Mark as Hired"
                    checked={isHired}
                    onChange={(value) => handleToggleRole("isHired", value)}
                    disabled={isInitial || isRejected || isReusable}
                    hint="Successful end status."
                  />
                  <Toggle
                    label="Mark as Rejected"
                    checked={isRejected}
                    onChange={(value) => handleToggleRole("isRejected", value)}
                    disabled={isInitial || isHired || isReusable}
                    hint="Terminal status."
                  />
                  <Toggle
                    label="Mark as Reusable"
                    checked={isReusable}
                    onChange={(value) => handleToggleRole("isReusable", value)}
                    disabled={isInitial || isHired || isRejected}
                    hint="Shared waypoint any stage can enter."
                  />
                </div>
              </div>

              <div className="rounded-2xl border border-border-primary bg-background-form p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-text-primary">Interview Scheduling</h3>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    Candidates currently at this status can have an interview scheduled from the
                    Interview Scheduling page. Not allowed on Hired/Rejected statuses (terminal outcomes).
                  </p>
                </div>
                <Toggle
                  label="Allow Interview Scheduling from this status"
                  checked={isInterviewEligible}
                  onChange={(value) => handleToggleRole("isInterviewEligible", value)}
                  disabled={isHired || isRejected}
                  hint="Independent of Initial/Reusable — a status can be both."
                />
              </div>

              <div className="rounded-2xl border border-border-primary bg-background-form p-4">
                <div className="mb-3">
                  <h3 className="text-sm font-semibold text-text-primary">
                    Allowed Transitions
                  </h3>
                  <p className="mt-1 text-[11px] text-text-secondary">
                    Select the next possible stages. Transitions must move forward in sequence unless the target is reusable.
                  </p>
                </div>

                {isRejected || isHired ? (
                  <div className="text-xs text-text-secondary">
                    Hired and rejected statuses do not allow outgoing transitions.
                  </div>
                ) : loadingOptions ? (
                  <div className="text-xs text-text-secondary">
                    Loading transition options...
                  </div>
                ) : transitionOptions.length === 0 ? (
                  isEdit ? (
                    <div className="text-xs text-text-secondary">
                      No other statuses available.
                    </div>
                  ) : (
                    // A status created at the end of the pipeline has nothing
                    // ahead of it to point at, and the server accepts that. The
                    // flow stays unusable until someone goes back and adds the
                    // edge, and nothing else on this screen says so.
                    <Callout tone="neutral" size="xs" icon={Info}>
                      Nothing to point at yet. Once the next status exists, edit this one to
                      allow the move into it.
                    </Callout>
                  )
                ) : (
                  <div className="max-h-64 overflow-y-auto overflow-x-hidden pr-1 custom-scrollbar">
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {transitionOptions.map((item) => (
                        <label
                          key={item.value}
                          className="flex items-start gap-2 rounded-xl border border-border-primary bg-background-secondary p-3 cursor-pointer"
                        >
                          <Input
                            type="checkbox"
                            checked={selectedTransitions.includes(item.value)}
                            onChange={() => handleTransitionToggle(item.value)}
                            className="mt-0.5 h-4 w-4 shrink-0 accent-[rgb(var(--color-btn-secondary))]"
                          />
                          <div className="min-w-0 flex-1">
                            <div className="truncate text-xs font-medium text-text-primary">
                              {toText(item.label)}
                            </div>
                            <div className="mt-1 break-words text-[11px] text-text-secondary">
                              {toText(item.code)} · Step {item.sequence ?? "?"} · {toText(item.type)}
                            </div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {inlineError ? (
                <div className="rounded-xl border border-[rgb(var(--color-error))]/20 bg-[rgb(var(--color-error))]/10 px-3 py-2 text-xs font-medium text-[rgb(var(--color-error))]">
                  {toText(inlineError)}
                </div>
              ) : null}
            </div>

            <div className="shrink-0 border-t border-border-primary bg-background-secondary px-5 py-4">
              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  onClick={onModelClose}
                  className="w-full sm:w-auto rounded-full px-4 py-2 bg-background-icon text-text-primary"
                >
                  Cancel
                </Button>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full sm:w-auto rounded-full px-4 py-2 bg-button-secondary text-text-base disabled:opacity-60"
                >
                  {submitting
                    ? isEdit
                      ? "Updating..."
                      : "Creating..."
                    : isEdit
                    ? "Update Status"
                    : "Create Status"}
                </Button>
              </div>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditCandidateStatusModal;
